/**
 * Shared report types and constants.
 *
 * Centralizes types and display constants that were previously duplicated
 * across every section's Reports component.
 */

// ─── Types ──────────────────────────────────────────────────────────────────

export interface Report {
  id: string;
  name: string;
  description?: string;
  sectionId: string;
  folderId?: string | null;
  config: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  slug?: string;
}

export interface ReportFolder {
  id: string;
  name: string;
  sectionId: string;
  parentId?: string | null;
  createdAt: string;
}

export interface ReportWithLink {
  report: Report;
  shareLink?: { url: string };
}

export interface ReportListing {
  folders: ReportFolder[];
  reports: ReportWithLink[];
}

// ─── Display Constants ──────────────────────────────────────────────────────

/** Human-readable labels for date preset values. */
export const PRESET_LABELS: Record<string, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  last_7_days: 'Last 7 days',
  last_14_days: 'Last 14 days',
  last_28_days: 'Last 28 days',
  last_30_days: 'Last 30 days',
  last_90_days: 'Last 90 days',
  this_month: 'This month',
  last_month: 'Last month',
  this_year: 'This year',
};

/** Human-readable labels for time interval values. */
export const INTERVAL_LABELS: Record<string, string> = {
  d: 'Daily',
  w: 'Weekly',
  m: 'Monthly',
  days: 'Daily',
  weeks: 'Weekly',
  months: 'Monthly',
};

/** Badge color names used for report config badges. */
export type BadgeColor =
  | 'slate'
  | 'brand'
  | 'emerald'
  | 'amber'
  | 'violet'
  | 'rose'
  | 'sky';

/** Tailwind class strings for each badge color. */
export const BADGE_COLORS: Record<BadgeColor, string> = {
  slate: 'text-slate-600 bg-slate-100',
  brand: 'text-brand-600 bg-brand-50',
  emerald: 'text-emerald-700 bg-emerald-50',
  amber: 'text-amber-700 bg-amber-50',
  violet: 'text-violet-700 bg-violet-50',
  rose: 'text-rose-700 bg-rose-50',
  sky: 'text-sky-700 bg-sky-50',
};
